import { isAppError } from '@/lib/api/errors';
import type { FieldError, PublishBlocker } from '@/lib/api/types';

/**
 * Helpers for the CMS-driven-collections governance gates (contract §4). The backend rejects
 * unsafe lifecycle transitions with structured field errors; these read them out so the editors
 * can give a non-technical user a clear, specific message instead of a generic "validation
 * error" — and know exactly which field to fix.
 */

function details(error: unknown): FieldError[] {
  return isAppError(error) ? (error.details ?? []) : [];
}

/** True when any field error carries the given backend code (e.g. `required_for_ready`). */
export function hasErrorCode(error: unknown, code: string): boolean {
  return details(error).some((d) => d.code === code);
}

/** Arabic copy gate (`locale_incomplete`) tripped on publish. */
export function isLocaleIncomplete(error: unknown): boolean {
  return hasErrorCode(error, 'locale_incomplete');
}

/** Field keys missing for a `ready` family (`required_for_ready`), e.g. `hero_image_id`. */
export function requiredForReadyFields(error: unknown): string[] {
  return details(error)
    .filter((d) => d.code === 'required_for_ready')
    .map((d) => d.field);
}

/** A reserved-slug rejection (`reserved`) — the slug shadows a system route. */
export function isReservedSlug(error: unknown): boolean {
  return hasErrorCode(error, 'reserved');
}

/**
 * Product slugs blocking a family delete (block-and-warn cascade, §3.4). The backend returns a
 * `409 conflict` listing them in the `products` field error; the message may be a single slug or
 * a comma/space-separated list, so we split defensively.
 */
export function blockingProductSlugs(error: unknown): string[] {
  if (!isAppError(error) || error.code !== 'conflict') return [];
  return details(error)
    .filter((d) => d.field === 'products' || d.field.startsWith('products'))
    .flatMap((d) => d.message.split(/[,\s]+/))
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * A refused publish's field errors as blockers, so the standing notice can show them next to
 * the server's read-time `publish_blockers` until the next save refreshes those.
 */
export function publishBlockersFromError(error: unknown): PublishBlocker[] {
  return details(error).map((d) => ({ field: d.field, code: d.code, message: d.message }));
}

/**
 * The server's standing blockers plus anything the last refused publish added, deduped on
 * `code:field` with the server's copy winning — the server is the one that will actually refuse.
 */
export function mergeBlockers(
  fromRead: PublishBlocker[] | undefined,
  fromRefusal: PublishBlocker[],
): PublishBlocker[] {
  const out = [...(fromRead ?? [])];
  const seen = new Set(out.map((b) => `${b.code}:${b.field}`));
  for (const b of fromRefusal) {
    const k = `${b.code}:${b.field}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(b);
    }
  }
  return out;
}

/**
 * The doc a brand-new record starts from. The server reads an absent `ar_complete` as
 * complete so legacy rows are never blocked; a record created here has no Arabic yet, so it
 * says so explicitly and the publish gate (and the editor's switch) start from the truth.
 */
export function newRecordDoc(): Record<string, unknown> {
  return { ar_complete: false };
}
